/**
 * Daily mode — one shared effeh per day (Cairo time), Wordle-style.
 * Endless mode lives at /play; this is the share-grid-friendly daily ritual.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { EffehGame, attemptSquare, type EffehInput, type EffehResult } from '../components/EffehGame'

type Daily = {
  date: string
  effeh: EffehInput | null
  max_attempts: number
  reason?: string
}

export default function Daily() {
  const [daily, setDaily] = useState<Daily | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [result, setResult] = useState<EffehResult | null>(null)
  const [shareToast, setShareToast] = useState(false)

  useEffect(() => {
    fetch('/api/daily')
      .then((r) => r.json() as Promise<Daily>)
      .then((d) => setDaily(d))
      .catch((e: unknown) => setLoadErr(String(e)))
  }, [])

  const shareText = useMemo(() => {
    if (!result || !daily) return ''
    const bonusSuffix = result.bonusScore > 0 ? `  ⭐ +${result.bonusScore}` : ''
    return `إفيه يومي ${daily.date}\n🎬 ${attemptSquare(result.movieAttempt)}  🎭 ${attemptSquare(result.actorAttempt)}  ·  ${result.movieScore}/100${bonusSuffix}\nhttps://2efeh.com/daily`
  }, [result, daily])

  async function share() {
    try {
      await navigator.clipboard.writeText(shareText)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 1800)
    } catch { /* clipboard blocked */ }
  }

  if (loadErr) return <Shell><p className="text-rose-400 text-center mt-12">حصل خطأ: {loadErr}</p></Shell>
  if (!daily) return <Shell><p className="text-ink-500 text-center mt-12">…</p></Shell>
  if (!daily.effeh) {
    return (
      <Shell>
        <p className="text-ink-300 text-center mt-12">لسه مفيش إفيه النهارده.</p>
        {daily.reason && <p className="text-xs text-ink-500 mt-2 text-center">({daily.reason})</p>}
      </Shell>
    )
  }

  return (
    <Shell>
      <div className="text-center mb-6">
        <div className="text-xs uppercase tracking-[0.3em] text-amber-500">إفيه النهارده</div>
        <div className="mx-auto w-12 effeh-rule opacity-60 mt-2 mb-2" />
        <div className="text-xs text-ink-500">{daily.date}</div>
      </div>

      <EffehGame
        effeh={daily.effeh}
        puzzleKey={daily.date}
        maxAttempts={daily.max_attempts}
        onDone={setResult}
      />

      {result && (
        <div className="mt-6 effeh-fade-up">
          <button
            onClick={share}
            className="w-full py-3.5 rounded-md bg-amber-500 text-ink-900 font-bold text-base tracking-wide hover:bg-amber-300 transition"
          >
            شارك نتيجة اليوم 📋
          </button>
          {shareToast && <p className="text-center text-leaf-400 text-sm mt-2">اتنسخت!</p>}
          <pre className="mt-4 text-center text-sm text-ink-300 whitespace-pre-wrap font-body">{shareText}</pre>
          <div className="text-center mt-6">
            <Link to="/play" className="text-amber-300 hover:underline">← العب جولات مفتوحة</Link>
          </div>
        </div>
      )}
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-ink-700/50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl">🎬</span>
            <span className="font-display text-xl text-amber-300">إفيه</span>
          </Link>
          <Link to="/play" className="text-xs text-amber-300 hover:underline">→ جولات مفتوحة</Link>
        </div>
      </header>
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-8">{children}</main>
      <footer className="text-center text-xs text-ink-500 py-6">
        نسخة تجريبية · made with 💛 in Cairo
      </footer>
    </div>
  )
}
